import React, { useEffect, useState } from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import DashboardFooter from "./dashboard_footer";
import Canvas from "./dahboard_canvas";
import axios from "axios";
import localforage from "localforage";
import { useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface ClinicData {
    name: string;
    email: string;
    phone: string;
    website: string;
    town: string;
    county: string;
    type: string;
    status: string;
    id: number;
    institution_name: string;
    postcode: string;
    description: string;
}

const ClinicInfo: React.FC = () => {
    const [clinic, setClinic] = useState<ClinicData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [staffCount, setStaffCount] = useState(0);
    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "Facility Summary - Uzima";
        loadClinicData();
    }, []);

    const loadClinicData = async () => {
        setIsLoading(true);
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const clinic_name = await localforage.getItem("clinic_name") as string || "";
            const clinic_email = await localforage.getItem("clinic_email") as string || "";
            const inst_name = await localforage.getItem("institution_name") as string || "";

            setClinic({
                name: clinic_name,
                email: clinic_email,
                phone: '',
                website: '',
                town: '',
                county: '',
                type: 'Healthcare Facility',
                status: 'active',
                id: clinic_id as number || 0,
                institution_name: inst_name,
                postcode: '',
                description: '',
            });

            // Try loading detailed facility info from API
            try {
                const res = await axios.get(`${base_url}facilities/clinics/all`, { timeout: 10000 });
                if (res.data?.status === 'success' && Array.isArray(res.data.data)) {
                    const match = res.data.data.find((c: any) => c.id === clinic_id || c.name === clinic_name);
                    if (match) {
                        setClinic(prev => prev ? { ...prev, ...match, institution_name: inst_name } : prev);
                    }
                }
            } catch (e) { console.error("Error loading clinic details:", e); }

            // Try loading staff count
            try {
                const staffRes = await axios.get(`${base_url}facilities/dashboard/staff`, { timeout: 10000 });
                const d = staffRes.data;
                if (d?.status === 'success' && Array.isArray(d.data)) setStaffCount(d.data.length);
                else if (Array.isArray(d)) setStaffCount(d.length);
            } catch (e) { /* staff count optional */ }

        } catch (e) {
            console.error("Error:", e);
        } finally { setIsLoading(false); }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Facility Summary</h1>
                                <p>View details about your active facility</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadClinicData}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/clinics" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-list-ul"></i> All Facilities</Link>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: ACCENT }}></i>
                                    <p style={{ marginTop: 12, color: 'var(--uzima-shell-text-muted)' }}>Loading facility details...</p>
                                </div>
                            </div>
                        ) : clinic ? (
                            <>
                                {/* Stats */}
                                <div className="sc-stats" style={{ marginBottom: 20 }}>
                                    {[
                                        { label: 'Facility ID', val: `#${clinic.id}`, icon: 'bx bx-hash', cls: 'teal' },
                                        { label: 'Staff', val: staffCount, icon: 'bx bx-user-plus', cls: 'blue' },
                                        { label: 'Status', val: clinic.status || 'Active', icon: 'bx bx-check-circle', cls: 'amber' },
                                        { label: 'Type', val: clinic.type || 'Clinic', icon: 'bx bx-category', cls: 'violet' },
                                    ].map((s, i) => (
                                        <div className="sc-stat" key={i}>
                                            <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                            <div className="sc-stat-value">{s.val}</div>
                                            <div className="sc-stat-label">{s.label}</div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    {/* Main Info */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-info-circle me-2" style={{ color: ACCENT }}></i>Facility Information</div>
                                        </div>
                                        <div className="sc-card-body">
                                            {[
                                                { label: 'Facility Name', value: clinic.name },
                                                { label: 'Institution', value: clinic.institution_name },
                                                { label: 'Email', value: clinic.email || 'Not set' },
                                                { label: 'Phone', value: clinic.phone || 'Not set' },
                                                { label: 'Website', value: clinic.website || 'Not set' },
                                                { label: 'Location', value: [clinic.town, clinic.county].filter(Boolean).join(', ') || 'Not set' },
                                                { label: 'Postcode', value: clinic.postcode || 'Not set' },
                                            ].map((item, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 6 ? '1px solid var(--uzima-shell-border)' : 'none' }}>
                                                    <span style={{ fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--uzima-shell-text-muted)' }}>{item.label}</span>
                                                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--uzima-shell-text)', textAlign: 'right', maxWidth: '60%' }}>{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Quick Actions & Description */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        <div className="sc-card" style={{ margin: 0 }}>
                                            <div style={{ padding: '12px 18px', borderBottom: '2px solid #3b82f6', background: 'rgba(59,130,246,0.03)' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-rocket me-2" style={{ color: '#3b82f6' }}></i>Quick Actions</div>
                                            </div>
                                            <div className="sc-card-body">
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                    {[
                                                        { label: 'Staff', icon: 'bx bx-user-plus', path: '/institution/professionals', color: '#3b82f6' },
                                                        { label: 'Services', icon: 'bx bx-briefcase', path: '/institution/service-center', color: ACCENT },
                                                        { label: 'Settings', icon: 'bx bx-cog', path: '/institution/settings', color: '#8b5cf6' },
                                                        { label: 'EHR', icon: 'bx bx-file', path: '/institution/ehr', color: '#d97706' },
                                                        { label: 'Management', icon: 'bx bx-buildings', path: '/institution/management', color: '#64748b' },
                                                        { label: 'Telehealth', icon: 'bx bx-video', path: '/institution/telehealth', color: '#e11d48' },
                                                    ].map((a, i) => (
                                                        <Link key={i} to={a.path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, textDecoration: 'none', background: `${a.color}08`, border: `1px solid ${a.color}20`, transition: 'all 0.15s' }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${a.color}15`; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${a.color}08`; }}
                                                        >
                                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${a.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <i className={a.icon} style={{ color: a.color, fontSize: '0.9rem' }}></i>
                                                            </div>
                                                            <span style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--uzima-shell-text)' }}>{a.label}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        {clinic.description && (
                                            <div className="sc-card" style={{ margin: 0 }}>
                                                <div style={{ padding: '12px 18px', borderBottom: '2px solid #8b5cf6', background: 'rgba(139,92,246,0.03)' }}>
                                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-notepad me-2" style={{ color: '#8b5cf6' }}></i>Description</div>
                                                </div>
                                                <div className="sc-card-body" dangerouslySetInnerHTML={{ __html: clinic.description }}></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <p style={{ fontWeight: 600 }}>No facility data available</p>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default ClinicInfo;
